import { Component } from '@theme/component';
import { lockScroll, unlockScroll } from '@theme/utilities';

/** Consent categories the banner asks for. Necessary cookies need no consent. */
const CATEGORIES = /** @type {const} */ (['preferences', 'analytics', 'marketing']);

const API_TIMEOUT = 10000;
const SAVE_TIMEOUT = 8000;

/** Links and buttons anywhere on the page that reopen the settings. */
const OPENER_HASHES = ['#cookie-einstellungen', '#cookie-settings'];
const OPENER_SELECTOR = ['[data-lj-cookie-settings]', ...OPENER_HASHES.map((hash) => `a[href$="${hash}"]`)].join(', ');

/**
 * @typedef {{ preferences: boolean, analytics: boolean, marketing: boolean }} Consent
 *
 * @typedef {object} CustomerPrivacy
 * @property {(consent: Partial<Consent>, callback: (result?: { error?: unknown }) => void) => void} setTrackingConsent
 * @property {() => Record<string, 'yes' | 'no' | ''>} currentVisitorConsent
 * @property {() => boolean} shouldShowBanner
 */

/** @type {Promise<CustomerPrivacy> | undefined} */
let privacyApiPromise;

/**
 * Loads Shopify's Customer Privacy API once per page.
 * `Shopify.loadFeatures` comes from `content_for_header`, which runs before this module.
 *
 * @returns {Promise<CustomerPrivacy>}
 */
function loadPrivacyApi() {
  privacyApiPromise ??= new Promise((resolve, reject) => {
    const shopify = /** @type {any} */ (window).Shopify;

    if (shopify?.customerPrivacy) {
      resolve(shopify.customerPrivacy);
      return;
    }

    if (typeof shopify?.loadFeatures !== 'function') {
      reject(new Error('Shopify.loadFeatures is not available'));
      return;
    }

    const timeout = setTimeout(() => reject(new Error('Customer Privacy API timed out')), API_TIMEOUT);

    shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.2' }], (/** @type {unknown} */ error) => {
      clearTimeout(timeout);

      if (error || !shopify.customerPrivacy) {
        reject(error instanceof Error ? error : new Error('Customer Privacy API failed to load'));
        return;
      }

      resolve(shopify.customerPrivacy);
    });
  }).catch((error) => {
    // A later save may try again, e.g. after a slow network recovered.
    privacyApiPromise = undefined;
    throw error;
  });

  return privacyApiPromise;
}

/**
 * Liebesjuwelier cookie banner on top of Shopify's Customer Privacy API.
 *
 * The visitor's choice is stored by Shopify only (`setTrackingConsent`), never in our own
 * cookie, so Shopify pixels, apps, analytics and checkout all apply it. The banner shows
 * while a category has no decision yet: for every visitor (`data-scope="all"`) or only
 * where Shopify's consent regions ask for it (`data-scope="regions"`).
 * With `data-preview` (theme editor) it always shows and saves nothing.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} banner
 * @property {HTMLDialogElement} dialog
 * @property {HTMLFormElement} form
 * @property {HTMLInputElement[]} switches
 * @property {HTMLElement} error
 * @property {HTMLElement} status
 *
 * @extends {Component<Refs>}
 */
class LjCookieBanner extends Component {
  requiredRefs = ['banner', 'dialog', 'form', 'switches', 'error', 'status'];

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {CustomerPrivacy | undefined} */
  #api;

  /** @type {HTMLElement | null} */
  #opener = null;

  #saving = false;

  get #isPreview() {
    return this.hasAttribute('data-preview');
  }

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const { dialog } = this.refs;

    document.addEventListener('click', this.#handleOpenerClick, { signal });
    dialog.addEventListener('close', this.#handleDialogClose, { signal });
    dialog.addEventListener('click', this.#handleBackdropClick, { signal });

    if (this.#isPreview) {
      this.#showBanner();
      return;
    }

    this.#init(signal);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    unlockScroll(this.refs.dialog);
  }

  /** @param {AbortSignal} signal */
  async #init(signal) {
    try {
      this.#api = await loadPrivacyApi();
    } catch (error) {
      console.error('[lj-cookie-banner]', error);
      return;
    }

    if (signal.aborted) return;

    if (this.#needsDecision()) {
      this.#showBanner();
    }

    // A menu link to /#cookie-einstellungen from another page opens the settings on arrival.
    if (OPENER_HASHES.includes(window.location.hash)) this.openPreferences();
  }

  #needsDecision() {
    const api = this.#api;
    if (!api) return false;

    const consent = api.currentVisitorConsent() ?? {};
    const undecided = CATEGORIES.some((category) => !consent[category]);

    if (!undecided) return false;
    if (this.dataset.scope === 'regions') return api.shouldShowBanner();

    return true;
  }

  #showBanner() {
    this.refs.banner.hidden = false;
  }

  #hideBanner() {
    const { banner } = this.refs;
    const hadFocus = banner.contains(document.activeElement);

    banner.hidden = true;

    if (hadFocus) this.#focusPage();
  }

  #focusPage() {
    const main = document.getElementById('MainContent');
    if (!main) return;

    if (!main.hasAttribute('tabindex')) main.setAttribute('tabindex', '-1');
    main.focus({ preventScroll: true });
  }

  /** Marks every category as granted. */
  acceptAll = () => {
    this.#save({ preferences: true, analytics: true, marketing: true });
  };

  /** Keeps necessary cookies only. */
  rejectAll = () => {
    this.#save({ preferences: false, analytics: false, marketing: false });
  };

  /**
   * Saves the switches of the settings dialog.
   * @param {SubmitEvent} event
   */
  saveSelection = (event) => {
    event.preventDefault();

    const consent = /** @type {Consent} */ ({ preferences: false, analytics: false, marketing: false });
    for (const input of this.refs.switches) {
      const name = /** @type {keyof Consent} */ (input.name);
      if (CATEGORIES.includes(name)) consent[name] = input.checked;
    }

    this.#save(consent);
  };

  /** Opens the settings dialog with the stored choice preselected. */
  openPreferences = () => {
    const { dialog, error } = this.refs;
    if (dialog.open) return;

    const active = document.activeElement;
    this.#opener = active instanceof HTMLElement && active !== document.body ? active : null;

    const stored = this.#api?.currentVisitorConsent() ?? {};
    for (const input of this.refs.switches) {
      input.checked = stored[input.name] === 'yes';
    }

    error.hidden = true;
    lockScroll(dialog);
    dialog.showModal();
  };

  /** Closes the settings dialog without saving. */
  closePreferences = () => {
    this.refs.dialog.close();
  };

  /** @param {MouseEvent} event */
  #handleOpenerClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const opener = event.target.closest(OPENER_SELECTOR);
    if (!opener) return;

    event.preventDefault();
    this.openPreferences();
  };

  /** @param {MouseEvent} event */
  #handleBackdropClick = (event) => {
    if (event.target === this.refs.dialog) this.closePreferences();
  };

  #handleDialogClose = () => {
    unlockScroll(this.refs.dialog);

    const opener = this.#opener;
    this.#opener = null;

    if (opener?.isConnected && opener.checkVisibility()) {
      opener.focus();
    } else {
      this.#focusPage();
    }
  };

  /** @param {Consent} consent */
  async #save(consent) {
    if (this.#saving) return;

    this.#saving = true;
    this.setAttribute('aria-busy', 'true');
    this.refs.error.hidden = true;

    try {
      if (!this.#isPreview) await this.#setTrackingConsent(consent);

      this.#hideBanner();
      if (this.refs.dialog.open) this.refs.dialog.close();
      this.#announce(this.dataset.savedMessage ?? '');
    } catch (error) {
      console.error('[lj-cookie-banner]', error);
      this.#showError();
    } finally {
      this.#saving = false;
      this.removeAttribute('aria-busy');
    }
  }

  /**
   * @param {Consent} consent
   * @returns {Promise<void>}
   */
  async #setTrackingConsent(consent) {
    const api = this.#api ?? (await loadPrivacyApi());
    this.#api = api;

    await new Promise((resolve, reject) => {
      const timeout = setTimeout(() => reject(new Error('setTrackingConsent timed out')), SAVE_TIMEOUT);

      api.setTrackingConsent(consent, (result) => {
        clearTimeout(timeout);

        if (result?.error) {
          reject(result.error instanceof Error ? result.error : new Error(String(result.error)));
        } else {
          resolve(undefined);
        }
      });
    });
  }

  /** Shows the error inside the dialog; opens it first when the banner was used. */
  #showError() {
    if (!this.refs.dialog.open) this.openPreferences();
    this.refs.error.hidden = false;
  }

  /** @param {string} message */
  #announce(message) {
    const { status } = this.refs;
    status.textContent = '';
    requestAnimationFrame(() => {
      status.textContent = message;
    });
  }
}

if (!customElements.get('lj-cookie-banner')) customElements.define('lj-cookie-banner', LjCookieBanner);
